import React, { useState } from 'react';

const AccordionSection = () => {
  // State to track which accordion item is open
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    // If clicking the same one, close it. If clicking a new one, open it.
    setOpenIndex(openIndex === index ? null : index);
  };

  // Accordion Data
  const faqData = [
    {
      id: 1,
      question: "How do I access my dashboard?",
      answer: "Once you sign up, you will receive an email with a verification link. Click the link to log in, and your personalized dashboard will be available immediately."
    },
    {
      id: 2,
      question: "Can I change my plan later?",
      answer: "Absolutely. You can upgrade, downgrade, or cancel your subscription at any time directly from your account settings. Changes take effect at the start of the next billing cycle."
    },
    {
      id: 3,
      question: "Is there a free trial available?",
      answer: "Yes, we offer a 14-day free trial for our Pro plan. No credit card is required to start, so you can explore all the features risk-free."
    },
    {
      id: 4,
      question: "How secure is my data?",
      answer: "Security is our top priority. We use industry-standard 256-bit encryption for data in transit and at rest, and we are fully GDPR and CCPA compliant."
    }
  ];

  return (
    <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          {/* --- Left Side: Content --- */}
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Quick Wins & Clear Path Forward
            </h2>
            
            <div className="mt-6 space-y-4">
              {/* Description 1 */}
              <p className="text-lg text-gray-600 leading-relaxed">
                Our QA and modernization efforts deliver tangible benefits within 30 days, giving you a solid foundation for further growth - without long-term commitments.
              </p>

              {/* Description 2 */}
              <p className="text-lg text-gray-600 leading-relaxed">
                We provide dedicated support 24/7 to ensure your business never stops. Explore our FAQ section to the right for quick answers, or contact us directly for personalized assistance.
              </p>
            </div>

            {/* 3 Check Icons with Text */}
            <div className="mt-8 space-y-5">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-md bg-blue-600 text-white">
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Analysis and roadmap</h4>
                  <p className="text-sm text-gray-500">Start your trial instantly without any commitment.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-md bg-blue-600 text-white">
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Cost estimates and recommendations for modernization</h4>
                  <p className="text-sm text-gray-500">No hidden fees or long-term contracts.</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-md bg-blue-600 text-white">
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">The freedom to decide your next steps - without upfront commitments</h4>
                  <p className="text-sm text-gray-500">Our team is always ready to help you succeed.</p>
                </div>
              </div>
            </div>
          </div>

          {/* --- Right Side: Accordion --- */}
          <div className="mt-12 lg:mt-0">
            <div className="space-y-4">
              {faqData.map((item, index) => (
                <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  
                  {/* Accordion Button/Header */}
                  <button
                    className="w-full px-6 py-4 bg-white text-left flex justify-between items-center focus:outline-none hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => toggleAccordion(index)}
                  >
                    <span className="text-lg font-medium text-gray-900">
                      {item.question}
                    </span>
                    {/* Plus/Minus Icon Toggle */}
                    <svg
                      className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
                        openIndex === index ? 'rotate-45' : 'rotate-0'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                  </button>

                  {/* Accordion Body (Animated) */}
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-gray-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AccordionSection;